/**
 * The AI assistant panel.
 *
 * TWO RULES THIS THING IS BUILT AROUND.
 *
 * 1. It answers from YOUR data, not from a model's memory of what backup
 *    software usually does. The question goes to a callable that assembles a
 *    factual snapshot of this org — counts, the failing machines, when each
 *    source last ran, which integrations are configured — and the model answers
 *    over that. A backup dashboard that confidently invents a status is worse
 *    than no dashboard, so the snapshot is built server-side from Firestore and
 *    the model is told, in the system prompt, to say it does not know rather
 *    than fill a gap.
 *
 * 2. It does not change anything on its own. It will walk you through setting up
 *    an integration or explain what a setting does, and it will tell you exactly
 *    where to click — but hiding a machine, writing a credential or changing a
 *    threshold stays a thing a person does deliberately. An assistant that can
 *    silently stop a backup being monitored is not a feature.
 */

import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import type { Summary, Target } from '../types';

/**
 * A suggestion the assistant attached to an answer.
 *
 * It is carried on the MESSAGE rather than in panel state because a
 * conversation can produce several, and the second one must not silently
 * replace the first while the user was still deciding about it.
 */
interface Idea { kind: 'bug' | 'feature'; title: string; body: string }
interface Msg { role: 'user' | 'assistant'; text: string; idea?: Idea | null; filed?: boolean }

/** Openers, so the empty state teaches what it is for instead of blinking. */
const SUGGESTIONS = [
  'What failed overnight?',
  'Which clients have stale backups?',
  'Why is this machine unknown?',
  'Help me set up an integration',
];

export function AiPanel({
  orgId,
  view,
  targets,
  counts,
  summary,
  onClose,
}: {
  orgId: string | null;
  view: string;
  targets: Target[];
  counts: { overall: { total: number; failed: number; warning: number } };
  summary: Summary | null;
  onClose: () => void;
}) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, busy]);

  /** Which message's suggestion is currently being filed, by index. */
  const [filing, setFiling] = useState<number | null>(null);

  async function file(i: number, idea: Idea) {
    if (!orgId) return;
    setFiling(i);
    setError(null);
    try {
      await httpsCallable(functions, 'submitFeatureRequest')({
        orgId,
        // The kind rides in the title because the board has no type field. A
        // schema change to carry one syllable is not worth the migration, and
        // a person scanning the list wants the word first anyway.
        title: (idea.kind === 'bug' ? 'Bug: ' : '') + idea.title,
        details: idea.body,
        view,
      });
      setMsgs((m) => m.map((msg, j) => (j === i ? { ...msg, filed: true } : msg)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be sent.');
    } finally {
      setFiling(null);
    }
  }

  async function ask(text: string) {
    const question = text.trim();
    if (!question || busy) return;
    setQ('');
    setError(null);
    setMsgs((m) => [...m, { role: 'user', text: question }]);
    setBusy(true);
    try {
      const r = await httpsCallable(functions, 'askAssistant')({
        orgId,
        question,
        // The view they are on, so "why is this failing" has a referent.
        view,
        // Prior turns only. The factual snapshot is rebuilt server-side every
        // time, so a stale number can never be carried forward in history.
        history: msgs.slice(-8),
      });
      const { answer, idea } = r.data as { answer: string; idea: Idea | null };
      setMsgs((m) => [...m, { role: 'assistant', text: answer, idea }]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'That did not go through.';
      setError(message);
      // Drop the unanswered question back into the box rather than losing it.
      setMsgs((m) => m.slice(0, -1));
      setQ(question);
    } finally {
      setBusy(false);
    }
  }

  const failing = targets.filter((t) => t.status === 'failed').length;

  return (
    <div className="backdrop open" onClick={onClose}>
      <div className="modal modal-wide ai-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hd">
          <div className="modal-ttl">Assistant</div>
          <button className="modal-x" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-bd ai-body">
          {msgs.length === 0 && (
            <div className="ai-intro">
              <p className="panel-note" style={{ marginTop: 0 }}>
                Ask about this organisation&rsquo;s backups. Answers come from your own data —
                {' '}{counts.overall.total} machines, {failing} currently failed — not from
                general knowledge about backup software. It will not change any setting for you;
                it will tell you where to change it.
              </p>
              <div className="ai-sugg">
                {SUGGESTIONS.map((s) => (
                  <button key={s} className="chip" onClick={() => void ask(s)}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {msgs.map((m, i) => (
            <div key={i} className={'ai-msg ai-' + m.role}>
              {m.text.split('\n').map((line, j) => <p key={j}>{line}</p>)}
              {m.idea && (
                <div className={'ai-idea' + (m.idea.kind === 'bug' ? ' ai-idea-bug' : '')}>
                  <div className="ai-idea-head">
                    {m.idea.kind === 'bug' ? 'This looks like a fault in the dashboard' : 'This is not something the dashboard does yet'}
                  </div>
                  <div className="ai-idea-title">{m.idea.title}</div>
                  <div className="ai-idea-body">{m.idea.body}</div>
                  {/* NOTHING IS FILED UNTIL SOMEBODY PRESSES THIS. The assistant
                      cannot change the estate, and it does not get to put work on
                      a shared board either - a board filling with unreviewed
                      machine-written requests is worth less than an empty one.
                      It noticed; a person decides. */}
                  {m.filed ? (
                    <div className="ai-idea-done">Sent. It is on the feature board now.</div>
                  ) : (
                    <button
                      className="btn"
                      disabled={filing === i}
                      onClick={() => void file(i, m.idea!)}
                    >
                      {filing === i ? 'Sending...' : 'Send this to the team'}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}

          {busy && <div className="ai-msg ai-assistant ai-thinking">Reading your data…</div>}
          {error && <div className="banner banner-error">{error}</div>}
          <div ref={endRef} />
        </div>

        <div className="modal-ft ai-ft">
          <input
            className="finput"
            value={q}
            placeholder={summary ? 'Ask about your backups…' : 'Loading your data…'}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(q); } }}
          />
          <button className="btn btn-primary" disabled={busy || !q.trim()} onClick={() => void ask(q)}>
            Ask
          </button>
        </div>
      </div>
    </div>
  );
}
